import { CoreInput, CoreSelect } from '@components'
import FileUploadInput from '@withdrawals/components/ProviderSpecificFieldInput/FileUploadInput'
import { ProviderSpecificFieldTypes } from 'mmfintech-backend-api'
import type { ProviderSpecificField } from 'mmfintech-backend-api/types'
import { humanizedEnumValue, tr } from 'mmfintech-commons'
import type { UseFormValuesOutput } from 'mmfintech-commons/types'
import { useMemo } from 'react'

type ProviderSpecificFieldProps = {
  setup: ProviderSpecificField
  formValues: UseFormValuesOutput
  className?: string
  beneficiaryName?: string
}

const ProviderSpecificFieldInput = (props: ProviderSpecificFieldProps) => {
  const { setup, formValues, className, beneficiaryName } = props

  const dataTest = `specific-field-${setup?.fieldName}`
  const inputLabel = useMemo(() => {
    const defaultLabel =
      setup?.defaultFieldNameKey ??
      (setup?.fieldNameKey ? humanizedEnumValue(setup?.fieldNameKey.split('.').pop()) : setup?.fieldName) ??
      ''
    return setup?.fieldNameKey ? tr(setup?.fieldNameKey, defaultLabel) : defaultLabel
  }, [setup])

  switch (setup?.type ?? '') {
    case ProviderSpecificFieldTypes.DROPDOWN: {
      const options = Array.isArray(setup?.permittedValues)
        ? setup.permittedValues.map(({ key, value }) => ({
            value,
            label: tr(key, value)
          }))
        : []

      return (
        <CoreSelect
          type='default'
          className={className}
          data-test={dataTest}
          label={inputLabel}
          options={options}
          {...formValues.registerInput(setup.fieldName)}
        />
      )
    }

    case ProviderSpecificFieldTypes.PHONE_INPUT:
      return (
        <CoreInput
          type='phone'
          className={className}
          data-test={dataTest}
          label={inputLabel}
          {...formValues.registerInput(setup.fieldName)}
          autoComplete='off'
        />
      )

    case ProviderSpecificFieldTypes.DATE_INPUT:
      return (
        <CoreInput
          type='date'
          className={className}
          data-test={dataTest}
          label={inputLabel}
          dataPickerForwardYears={20}
          // LeftIcon={<CalendarIcon />}
          {...formValues.registerInput(setup.fieldName)}
        />
      )

    case ProviderSpecificFieldTypes.STRING_INPUT: {
      const hint =
        setup.fieldName === 'paymentDescription'
          ? beneficiaryName
            ? tr(
                'FRONTEND.WITHDRAW.PROVIDER_SPECIFIC_FIELDS.PAYMENT_DESCRIPTION_HINT_WITH_NAME',
                'Include the beneficiary name in the description: {{name}}',
                { name: beneficiaryName }
              )
            : tr(
                'FRONTEND.WITHDRAW.PROVIDER_SPECIFIC_FIELDS.PAYMENT_DESCRIPTION_HINT',
                "Include the beneficiary's name in the description"
              )
          : undefined

      return (
        <CoreInput
          type='text'
          className={className}
          data-test={dataTest}
          label={inputLabel}
          hint={hint}
          {...formValues.registerInput(setup.fieldName)}
          autoComplete='off'
        />
      )
    }

    case ProviderSpecificFieldTypes.FILE_UPLOAD:
      return (
        <FileUploadInput
          className={className}
          label={inputLabel}
          documentType={setup.fieldName === 'paymentInvoice' ? 'INVOICE' : 'OTHER'}
          {...formValues.registerInput(setup.fieldName)}
          data-test={dataTest}
        />
      )
  }

  return null
}

export default ProviderSpecificFieldInput
